import { Box, Card, Typography } from '@mui/material'
import { C1Button, C1LoadingButton } from 'c1ui'
import { C1Dialog, C1DialogActions, C1DialogContent, C1DialogTitle } from 'components/core/dialog/C1Dialog'
import { C1LabeledTextFieldWithPresets } from 'components/core/RHKFormComponents/C1LabeledTextFieldWithPresets'
import { useStyles } from 'components/pages/task/action/form/styles'
import React, { FormEvent, KeyboardEvent, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

type optionalStringHandler = ((comment: string) => void) | null

type BaseActionFormProps = {
    closeDialog: () => void
    onApprove?: optionalStringHandler
    onComment?: optionalStringHandler
    onDeny?: optionalStringHandler
    title: string | null
    open: boolean
    description?: string
    actionText: string
    isActionLoading?: boolean
    presets?: string[]
}

export const BaseActionForm = ({
    closeDialog,
    onApprove,
    onComment,
    onDeny,
    title,
    open,
    description,
    actionText,
    presets,
    isActionLoading = false,
}: BaseActionFormProps): React.ReactElement => {
    const intl = useIntl()
    const [comment, setComment] = useState('')
    const [helperTextError, setHelperTextError] = useState<string | undefined>()
    const classes = useStyles()
    const commentInput = useRef<HTMLInputElement>(null)

    const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
        event.preventDefault()

        // If this form is used, a comment is always required.
        if (!comment || comment.replaceAll(' ', '').length === 0) {
            setHelperTextError(intl.formatMessage({
                id: 'action_form_comment_required',
                defaultMessage: 'A comment is required',
            }))
            return
        }

        if (onApprove) {
            onApprove(comment)
        } else if (onComment) {
            onComment(comment)
        } else if (onDeny) {
            onDeny(comment)
        }
        setComment('')
    }

    const handleCommentChange = (): void => {
        setComment(commentInput.current?.value || '')
    }

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
        if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
            event.preventDefault()
            handleSubmit(event as unknown as FormEvent<HTMLFormElement>)
        }
    }

    return (
        <C1Dialog open={open} onClose={closeDialog}>
            <Card classes={classes}>
                <form onSubmit={handleSubmit}>
                    <C1DialogTitle>{title}</C1DialogTitle>
                    <C1DialogContent>
                        <Box mb={4}>
                            <Typography>
                                {description}
                            </Typography>
                        </Box>
                        <C1LabeledTextFieldWithPresets
                            name="comment"
                            label={<FormattedMessage id="component_task_action_form_comment_label" defaultMessage="Comment" />}
                            presets={presets}
                            value={comment}
                            setValue={setComment}
                            autoFocus
                            id="comment"
                            inputRef={commentInput}
                            onChange={handleCommentChange}
                            onKeyDown={handleKeyDown}
                            type="text"
                            fullWidth
                            multiline
                            error={!!helperTextError}
                            helperText={
                                helperTextError || (
                                    <Typography variant="caption" color="textSecondary">
                                        <FormattedMessage
                                            id="components_tasks_audit_comment_form_markdown_hint"
                                            defaultMessage="Tip: Use Markdown to format your comments"
                                        />
                                    </Typography>
                                )
                            }
                        />
                    </C1DialogContent>
                    <C1DialogActions>
                        <C1Button
                            onClick={(): void => {
                                closeDialog()
                            }}
                            variant="outlined"
                            type="button"
                        >
                            <FormattedMessage id="component_task_action_form_comment_cancel" defaultMessage="Go back" />
                        </C1Button>
                        <C1LoadingButton
                            type="submit"
                            color="primary"
                            variant="contained"
                            loading={isActionLoading}
                        >
                            {actionText}
                        </C1LoadingButton>
                    </C1DialogActions>
                </form>
            </Card>
        </C1Dialog>
    )
}
